import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { SUBSCRIPTION_PRESETS, SubscriptionPreset } from '@libs/config/subscription-presets';
import type { SubscriptionDirectDTO } from '@libs/dtos/order/order-request.dto';
import { ProductResponseDTO, PublicProductResponseDTO } from '@libs/dtos/product/product.dto';
import { CurrentUserService } from '@services/auth/current-user.service';
import { BrandingInfo, BrandingService } from '@services/branding/branding.service';
import { DiscountPreview, PricingService } from '@services/checkout/pricing.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ValidationService } from '@services/ui/validation.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import {
	ManualPaymentConfirmed,
	ManualPaymentSelected,
	PaymentMethod,
	PaymentMethodModalComponent,
} from '@shared/components/payment-method-modal/payment-method-modal.component';
import { ProductCatalogService } from '@shared/components/product-picker/product-catalog.service';
import { ProductPickerComponent } from '@shared/components/product-picker/product-picker.component';
import { SharedModule } from '@shared/modules/shared.module';
import { platformLinkPlaceholder } from 'app/core/constants/platform-brand.constants';
import { debounceTime, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { SubscriptionService } from '../../../core/services/subscriptions/subscription.service';
import { subscriptionIntervalLabel } from '../../backoffice/subscriptions/subscription-interval';

/** Whole numbers only (units, deliveries, minutes) */
const WHOLE_NUMBER_PATTERN = /^\d+$/;
/** Custom plan limits */
const MAX_CUSTOM_RUNS = 1000;
const MAX_INTERVAL_MINUTES = 525600; // one year

@Component({
	selector: 'app-subscriptions',
	imports: [SharedModule, InputComponent, ButtonComponent, PaymentMethodModalComponent, ProductPickerComponent],
	templateUrl: './subscriptions.component.html',
	styleUrl: './subscriptions.component.scss',
	// Catalog and price of this form (one set per page)
	providers: [ProductCatalogService, PricingService],
})
export class SubscriptionsComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	/** Platform → product → details of the form */
	readonly catalog = inject(ProductCatalogService);

	get linkPlaceholder(): string {
		return platformLinkPlaceholder(this.catalog.selectedPlatformName);
	}
	/** Price per delivery from the API and the automatic discount on the total */
	readonly pricing = inject(PricingService);

	subscriptionForm: FormGroup;
	presets = SUBSCRIPTION_PRESETS;
	selectedPreset: SubscriptionPreset | null = null;

	branding: BrandingInfo | null = null;

	creatingSubscription = false;

	/** Price per delivery × deliveries, before the discount (0 = no price) */
	totalPrice = 0;

	// Payment modal
	showPaymentModal = false;
	pendingSubscriptionData: SubscriptionDirectDTO | null = null;
	pendingSubscriptionId: number | null = null;
	isCreatingManualSubscription = false;

	constructor(
		private fb: FormBuilder,
		private subscriptionService: SubscriptionService,
		private currentUserService: CurrentUserService,
		private messageAlertService: MessageAlertService,
		private brandingService: BrandingService,
		private translocoService: TranslocoService,
		private validationService: ValidationService,
		private router: Router,
	) {
		this.subscriptionForm = this.fb.group({
			platform: ['', Validators.required],
			product: ['', Validators.required],
			amountPerRun: ['', this.amountValidators()],
			link: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
			totalRuns: [
				{ value: '', disabled: true },
				[
					Validators.required,
					Validators.min(1),
					Validators.max(MAX_CUSTOM_RUNS),
					Validators.pattern(WHOLE_NUMBER_PATTERN),
				],
			],
			intervalMinutes: [
				{ value: '', disabled: true },
				[
					Validators.required,
					Validators.min(1),
					Validators.max(MAX_INTERVAL_MINUTES),
					Validators.pattern(WHOLE_NUMBER_PATTERN),
				],
			],
		});
	}

	/** Units per delivery: whole number within the selected product's min/max (as boost does) */
	private amountValidators() {
		const validators = [Validators.required, Validators.min(1), Validators.pattern(WHOLE_NUMBER_PATTERN)];
		if (this.selectedProductDetails?.minAmount) {
			validators.push(Validators.min(this.selectedProductDetails.minAmount));
		}
		if (this.selectedProductDetails?.maxAmount) {
			validators.push(Validators.max(this.selectedProductDetails.maxAmount));
		}
		return validators;
	}

	private updateAmountValidators() {
		const control = this.subscriptionForm.get('amountPerRun');
		control?.setValidators(this.amountValidators());
		control?.updateValueAndValidity({ emitEvent: false });
	}

	ngOnInit() {
		this.setupFormSubscriptions();
		this.brandingService
			.load()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (branding) => {
					this.branding = branding;
					this.catalog.loadPlatforms();
				},
				error: () => this.catalog.loadPlatforms(),
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get backgroundImage(): string | null {
		return this.branding?.boostBackgroundImage || null;
	}

	private setupFormSubscriptions() {
		this.catalog.connect(
			this.subscriptionForm,
			{
				publicCatalog: false,
				lockWhileLoading: false,
				// Units and price belong to the previous platform: no stale total
				onPlatformChange: () => {
					this.subscriptionForm.patchValue({ amountPerRun: '' });
					this.subscriptionForm.get('amountPerRun')?.markAsUntouched();
					this.updateAmountValidators();
					this.clearPrice();
				},
				onProductChange: () => {
					this.updateAmountValidators();
					this.clearPrice();
				},
				onProductLoaded: () => {
					this.updateAmountValidators();
					this.calculatePrice();
				},
				onPlatformsLoaded: () => {
					if (this.catalog.platforms.length > 0) {
						this.subscriptionForm.patchValue({ platform: this.catalog.platforms[0].value });
					}
				},
			},
			this.destroy$,
		);

		// Wait until the user stops typing: one price request, not one per keystroke
		for (const field of ['amountPerRun', 'totalRuns']) {
			this.subscriptionForm
				.get(field)
				?.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
				.subscribe(() => {
					this.calculatePrice();
				});
		}
	}

	// Catalog state used by the form and the summary
	get selectedPlatform(): string {
		return this.catalog.selectedPlatform;
	}

	get selectedProduct(): string {
		return this.catalog.selectedProduct;
	}

	get selectedProductDetails(): ProductResponseDTO | PublicProductResponseDTO | null {
		return this.catalog.selectedProductDetails;
	}

	// Price state (PricingService) as the template reads it
	/** Price of one delivery */
	get pricePerRun(): number {
		return this.pricing.price;
	}

	/** /calculate-price failed: no price is shown, a retry is offered */
	get priceError(): boolean {
		return this.pricing.priceError;
	}

	/** Automatic discount the backend applies to the total (null when none) */
	get discount(): DiscountPreview | null {
		return this.pricing.discount;
	}

	selectPreset(preset: SubscriptionPreset) {
		this.selectedPreset = preset;
		if (preset.id === 'custom') {
			this.subscriptionForm.get('totalRuns')?.enable();
			this.subscriptionForm.get('intervalMinutes')?.enable();
			this.subscriptionForm.patchValue({ totalRuns: '', intervalMinutes: '' });
		} else {
			this.subscriptionForm.get('totalRuns')?.disable();
			this.subscriptionForm.get('intervalMinutes')?.disable();
			this.subscriptionForm.patchValue({
				totalRuns: preset.runs,
				intervalMinutes: preset.intervalMinutes,
			});
		}
		this.calculatePrice();
	}

	get totalRuns(): number {
		if (this.selectedPreset?.id === 'custom') {
			const control = this.subscriptionForm.get('totalRuns');
			return control?.valid ? Number(control.value) : 0;
		}
		return this.selectedPreset?.runs || 0;
	}

	get intervalMinutes(): number {
		if (this.selectedPreset?.id === 'custom') {
			const control = this.subscriptionForm.get('intervalMinutes');
			return control?.valid ? Number(control.value) : 0;
		}
		return this.selectedPreset?.intervalMinutes || 0;
	}

	get intervalLabel(): string {
		return this.formatInterval(this.intervalMinutes);
	}

	/** "Cada 2 días" / "Every 1 hour"... translated with singular/plural */
	formatInterval(minutes: number): string {
		return subscriptionIntervalLabel(minutes, this.translocoService);
	}

	/** Only a valid amount (whole, within the product limits) is priced */
	get amountPerRun(): number {
		const control = this.subscriptionForm.get('amountPerRun');
		return control?.valid ? Number(control.value) : 0;
	}

	get totalAmount(): number {
		return this.amountPerRun * this.totalRuns;
	}

	/**
	 * Price of the plan: the price of one delivery (product + units, from the API) × deliveries.
	 * The price per delivery only depends on product + units, so changing the deliveries reuses it.
	 */
	private calculatePrice() {
		if (!this.selectedProductDetails || !this.amountPerRun || !this.totalRuns) {
			this.clearPrice(); // an answer still in flight is no longer valid
			return;
		}

		const productId = Number(this.selectedProductDetails.id);
		this.pricing.quote(
			productId,
			this.amountPerRun,
			(pricePerRun) => this.setTotal(Math.round(pricePerRun * this.totalRuns * 100) / 100),
			{
				reuse: true,
				onError: () => {
					this.totalPrice = 0;
				},
			},
		);
	}

	/** Retry button of the price error */
	retryPrice(): void {
		this.calculatePrice();
	}

	/** Total before discount + preview of the automatic discount the backend will apply to it */
	private setTotal(total: number) {
		this.totalPrice = total;
		this.pricing.discount = null;
		this.pricing.previewDiscount(total);
	}

	/** What the user pays: the total with the automatic discount */
	get finalPrice(): number {
		return this.discount ? this.discount.finalPrice : this.totalPrice;
	}

	private clearPrice() {
		this.pricing.clear();
		this.totalPrice = 0;
	}

	get canSubmit(): boolean {
		const formValid = this.subscriptionForm.valid && this.amountPerRun > 0 && this.selectedProduct;
		return !!(formValid && this.selectedPreset && this.totalRuns > 0 && this.intervalMinutes > 0);
	}

	isFieldInvalid(field: string): boolean {
		const control = this.subscriptionForm.get(field);
		return !!(control?.invalid && control.touched);
	}

	getFieldError(field: string): string {
		const control = this.subscriptionForm.get(field);
		if (!control?.errors || !control.touched) return '';
		const [key, value] = Object.entries(control.errors)[0];
		if (key === 'pattern') {
			return field === 'link'
				? this.translocoService.translate('boost.errors.invalidUrl')
				: this.validationService.getValidatorErrorMessage('integer');
		}
		return this.validationService.getValidatorErrorMessage(key, value);
	}

	get selectedProductName(): string {
		return this.catalog.selectedProductName;
	}

	async onSubmit() {
		if (this.creatingSubscription) return;
		if (!this.canSubmit) {
			// Say why: field errors under each input and a notice
			this.subscriptionForm.markAllAsTouched();
			this.messageAlertService.showMessage(
				'warn',
				this.translocoService.translate('common.labels.warning'),
				this.translocoService.translate('boost.messages.fillRequired'),
			);
			return;
		}

		const currentUser = this.currentUserService.getCurrentUser();
		if (!currentUser?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('subscriptions.messages.notAuthenticated'),
			);
			return;
		}

		const subscriptionData: SubscriptionDirectDTO = {
			userId: currentUser.userId.toString(),
			platformId: this.selectedPlatform,
			productId: this.selectedProduct,
			url: this.subscriptionForm.get('link')?.value,
			amountPerRun: this.amountPerRun,
			totalRuns: this.totalRuns,
			intervalMinutes: this.intervalMinutes,
		};

		// Admin and Root create the subscription directly (no payment)
		const isAdmin = this.currentUserService.hasPermissionOrAdmin('orders:create-direct');

		if (isAdmin) {
			await this.createDirectSubscription(subscriptionData);
		} else if (this.totalPrice <= 0) {
			// No price from the API yet (or it failed): the payment would show a total of 0
			this.pricing.priceError = true;
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('common.states.priceError'),
			);
		} else {
			this.pendingSubscriptionData = subscriptionData;
			this.showPaymentModal = true;
		}
	}

	private async createDirectSubscription(data: SubscriptionDirectDTO) {
		this.creatingSubscription = true;
		try {
			const response = await firstValueFrom(this.subscriptionService.createDirect(data));
			if (response?.success) {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('subscriptions.messages.createdTitle'),
					this.translocoService.translate('subscriptions.messages.createdMessage', {
						id: response.subscriptionId,
					}),
				);
				this.resetForm();
			} else {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('subscriptions.messages.createError'),
				);
			}
		} catch {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('subscriptions.messages.createError'),
			);
		} finally {
			this.creatingSubscription = false;
		}
	}

	async onPaymentMethodSelected(method: PaymentMethod) {
		if (!this.pendingSubscriptionData) return;

		if (method === 'card') {
			await this.processStripePayment(this.pendingSubscriptionData);
			this.pendingSubscriptionData = null;
		}
	}

	async onManualPaymentSelected(event: ManualPaymentSelected) {
		if (!this.pendingSubscriptionData) return;

		this.isCreatingManualSubscription = true;
		try {
			const response = await firstValueFrom(
				this.subscriptionService.createPending({
					...this.pendingSubscriptionData,
					paymentMethod: event.method,
				}),
			);

			if (response?.success && response.paid) {
				// Total 0 €: already paid and active, no payment instructions to show
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('subscriptions.messages.createdTitle'),
					this.translocoService.translate('subscriptions.messages.createdMessage', {
						id: response.subscriptionId,
					}),
				);
				this.resetForm();
				this.resetPaymentModalState();
				this.router.navigate(['/my-subscriptions']);
			} else if (response?.success) {
				this.pendingSubscriptionId = response.subscriptionId;
			} else {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('subscriptions.messages.createError'),
				);
			}
		} catch {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('subscriptions.messages.createError'),
			);
		} finally {
			this.isCreatingManualSubscription = false;
		}
	}

	async onManualPaymentConfirmed(event: ManualPaymentConfirmed) {
		if (!event.orderId) return;
		try {
			await firstValueFrom(this.subscriptionService.confirmPayment(event.orderId));
			this.messageAlertService.showMessage(
				'success',
				this.translocoService.translate('subscriptions.messages.paymentConfirmedTitle'),
				this.translocoService.translate('subscriptions.messages.paymentConfirmedMessage'),
			);
			this.resetForm();
			this.resetPaymentModalState();
		} catch {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('subscriptions.messages.confirmPaymentError'),
			);
		}
	}

	onPaymentModalCancelled() {
		this.resetPaymentModalState();
	}

	private async processStripePayment(data: SubscriptionDirectDTO) {
		this.creatingSubscription = true;
		try {
			const response = await firstValueFrom(this.subscriptionService.createCheckout(data));
			if (response?.checkoutUrl) {
				this.subscriptionService.redirectToCheckout(response.checkoutUrl);
			} else {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('subscriptions.messages.paymentUrlError'),
				);
			}
		} catch {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('subscriptions.messages.createError'),
			);
		} finally {
			this.creatingSubscription = false;
		}
	}

	private resetForm() {
		this.subscriptionForm.patchValue({ amountPerRun: '', link: '', totalRuns: '', intervalMinutes: '' });
		this.subscriptionForm.get('totalRuns')?.disable();
		this.subscriptionForm.get('intervalMinutes')?.disable();
		this.subscriptionForm.markAsUntouched();
		this.selectedPreset = null;
		this.clearPrice();
	}

	private resetPaymentModalState() {
		this.showPaymentModal = false;
		this.pendingSubscriptionData = null;
		this.pendingSubscriptionId = null;
		this.isCreatingManualSubscription = false;
	}
}
